"use client";

import { router } from "@inertiajs/react";
import { ChevronDown, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import AsyncCombobox, {
  type AsyncOption,
} from "@/components/async-combobox";
import { DataTablePagination } from "@/components/data-table-pagination";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { useDataTable } from "@/hooks/use-data-table";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import { useSyncedDebouncedValue } from "@/hooks/use-synced-debounced-value";
import { DataTable } from "@/lib/data-table";
import type { PagyMetadata, Product, RansackFilters } from "@/types";

import { buildColumns } from "./columns";

interface ProductsDataTableProps {
  products: Product[];
  pagy: PagyMetadata;
  filters: RansackFilters;
  organizationSlug: string;
}

export function ProductsDataTable({
  products,
  pagy,
  filters,
  organizationSlug,
}: ProductsDataTableProps) {
  const routes = useOrganizationRoutes();
  // Use shared data table logic for common operations
  const {
    isLoading,
    handleFilterChange,
    handlePageChange,
    handleSortingChange,
    getSortingState,
  } = useDataTable({
    filters,
    pagy,
    path: routes.products.index(),
  });
  const columns = buildColumns();

  // Product-specific filter state
  const [searchMode, setSearchMode] = useState<
    "id" | "internal_code" | "name"
  >("name");
  const {
    value: searchValue,
    setValue: setSearchValue,
    debouncedValue: debouncedSearchValue,
  } = useSyncedDebouncedValue(
    filters.id_eq ??
      filters.internal_code_cont ??
      filters.normalized_name_cont ??
      "",
  );
  const [categoryOption, setCategoryOption] = useState<AsyncOption | null>(null);
  const categoryOptionValue = categoryOption
    ? String(categoryOption.value)
    : undefined;

  useEffect(() => {
    const currentCategoryId = filters.category_id_eq;

    if (!currentCategoryId) {
      if (categoryOptionValue) {
        setCategoryOption(null);
      }
      return;
    }

    const normalizedCurrentId = String(currentCategoryId);

    if (
      categoryOptionValue &&
      categoryOptionValue === normalizedCurrentId
    ) {
      return;
    }

    let isMounted = true;

    const fetchSelectedCategory = async () => {
      try {
        const params = new URLSearchParams();
        params.append("ids[]", normalizedCurrentId);
        const response = await fetch(
          `/organizations/${organizationSlug}/categories/search?${params.toString()}`,
          {
          headers: {
            Accept: "application/json",
          },
          },
        );
        if (!response.ok) return;
        const data = (await response.json()) as AsyncOption[];
        const match = data.find(
          (option) => String(option.value) === normalizedCurrentId,
        );
        if (isMounted) {
          setCategoryOption(
            match
              ? { value: String(match.value), label: match.label }
              : null,
          );
        }
      } catch {
        if (isMounted) {
          setCategoryOption(null);
        }
      }
    };

    void fetchSelectedCategory();

    return () => {
      isMounted = false;
    };
  }, [filters.category_id_eq, categoryOptionValue]);

  const loadCategoryOptions = useCallback(
    async (inputValue: string): Promise<AsyncOption[]> => {
      const params = new URLSearchParams();
      if (inputValue) {
        params.set("term", inputValue);
      }

      const idsToInclude = new Set<string>();
      if (filters.category_id_eq) {
        idsToInclude.add(String(filters.category_id_eq));
      }
      if (categoryOptionValue) {
        idsToInclude.add(categoryOptionValue);
      }
      idsToInclude.forEach((id) => params.append("ids[]", id));

      const queryString = params.toString();
      const url = queryString
        ? `/organizations/${organizationSlug}/categories/search?${queryString}`
        : `/organizations/${organizationSlug}/categories/search`;

      try {
        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
          },
        });
        if (!response.ok) {
          return [];
        }
        const data = (await response.json()) as AsyncOption[];
        return data.map((option) => ({
          value: String(option.value),
          label: option.label,
        }));
      } catch {
        return [];
      }
    },
    [filters.category_id_eq, categoryOptionValue, organizationSlug],
  );

  const handleCategorySelect = useCallback(
    (option: AsyncOption | null) => {
      setCategoryOption(
        option ? { value: String(option.value), label: option.label } : null,
      );
      handleFilterChange({
        category_id_eq: option ? String(option.value) : undefined,
      });
    },
    [handleFilterChange],
  );

  useEffect(() => {
    if (filters.id_eq) {
      setSearchMode("id");
    } else if (filters.internal_code_cont) {
      setSearchMode("internal_code");
    } else if (filters.normalized_name_cont) {
      setSearchMode("name");
    }
  }, [
    filters.id_eq,
    filters.internal_code_cont,
    filters.normalized_name_cont,
  ]);

  useEffect(() => {
    const normalized = debouncedSearchValue.trim();
    const nextValue = normalized ? normalized : undefined;

    if (searchMode === "id") {
      if ((filters.id_eq ?? undefined) !== nextValue) {
        handleFilterChange({
          id_eq: nextValue,
          internal_code_cont: undefined,
          normalized_name_cont: undefined,
        });
      }
    } else if (searchMode === "internal_code") {
      if ((filters.internal_code_cont ?? undefined) !== nextValue) {
        handleFilterChange({
          internal_code_cont: nextValue,
          id_eq: undefined,
          normalized_name_cont: undefined,
        });
      }
    } else {
      if ((filters.normalized_name_cont ?? undefined) !== nextValue) {
        handleFilterChange({
          normalized_name_cont: nextValue,
          id_eq: undefined,
          internal_code_cont: undefined,
        });
      }
    }
    // Only depend on debounced value + searchMode to avoid loops when filters change from the server.
  }, [debouncedSearchValue, searchMode]);

  const clearFilters = () => {
    setSearchValue("");
    setCategoryOption(null);
    router.get(
      window.location.pathname,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: ["data", "filters"],
        reset: ["data"],
      },
    );
  };

  const hasCategoryFilter =
    Boolean(categoryOptionValue) || Boolean(filters.category_id_eq);

  return (
    <div className="space-y-4">
      {/* External Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <ButtonGroup>
          <InputGroup className="w-[280px]">
            <InputGroupInput
              placeholder={
                searchMode === "id"
                  ? "Buscar por ID..."
                  : searchMode === "internal_code"
                    ? "Buscar por código..."
                    : "Buscar por nombre..."
              }
              type={searchMode === "id" ? "number" : "text"}
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
            />
            <InputGroupAddon align="inline-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <InputGroupButton variant="ghost" className="!pr-1.5 text-xs">
                    {searchMode === "id"
                      ? "ID"
                      : searchMode === "internal_code"
                        ? "Código"
                        : "Nombre"}{" "}
                    <ChevronDown className="size-3" />
                  </InputGroupButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setSearchMode("id")}>
                    ID
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSearchMode("internal_code")}>
                    Código Interno
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSearchMode("name")}>
                    Nombre
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </InputGroupAddon>
          </InputGroup>
          <AsyncCombobox
            value={categoryOption}
            onChange={handleCategorySelect}
            loadOptions={loadCategoryOptions}
            defaultOptions={true}
            cacheOptions={true}
            placeholder="Filtrar por categoría"
            buttonClassName="w-[200px] rounded-l-none border-l-0"
          />
        </ButtonGroup>

        {(searchValue || hasCategoryFilter) && (
          <Button variant="outline" onClick={clearFilters}>
            <Trash2 className="h-4 w-4" /> Limpiar filtros
          </Button>
        )}
      </div>

      {/* DataTable */}
      <div className={isLoading ? "opacity-50" : ""}>
        <DataTable
          columns={columns}
          data={products}
          sorting={getSortingState()}
          onSortingChange={handleSortingChange}
          manualSorting={true}
          manualPagination={true}
          pageCount={pagy.pages}
          pagination={false}
          onRowClick={(product) => router.visit(routes.products.show(product))}
        />
      </div>

      {/* Server-side pagination */}
      <DataTablePagination
        pagy={pagy}
        isLoading={isLoading}
        onPageChange={handlePageChange}
        entityName="productos"
      />
    </div>
  );
}
